"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, InputField } from "@vionixasia/shared-components";
import { AdminShell } from "@/components/AdminShell";
import { apiClient } from "@/lib/api-client";
import { useQueryParam } from "@/lib/use-query-param";
import type { ResultEvent } from "@/types/event";

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function NewEventPage() {
  const navigate = useNavigate();
  const projectId = useQueryParam("projectId");
  const [form, setForm] = useState({
    name: "",
    slug: "",
    date: "",
    location: "",
    organizer_name: "",
    result_base_url: "",
    whatsapp_template_name: "finish_result",
    status: "draft",
  });
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!projectId) return;
    setSaving(true);
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    const data = await apiClient<{ event: ResultEvent }>(`/api/projects/${projectId}/events`, { method: "POST", body: JSON.stringify(payload) });
    navigate(`/dashboard/event?projectId=${projectId}&eventId=${data.event.id}`, { replace: true });
  }

  return (
    <AdminShell>
      <h1 className="text-2xl font-semibold text-ink">Create Event</h1>
      <form onSubmit={submit} className="mt-6 grid max-w-3xl gap-4 rounded-card border border-line bg-white p-5 sm:grid-cols-2">
        {[
          ["name", "Event name"],
          ["slug", "Event slug"],
          ["date", "Event date"],
          ["location", "Location"],
          ["organizer_name", "Organizer name"],
          ["result_base_url", "Result base URL"],
          ["whatsapp_template_name", "WhatsApp template name"],
        ].map(([key, label]) => (
          <InputField
            key={key}
            label={label}
            name={key}
            type={key === "date" ? "date" : "text"}
            value={form[key as keyof typeof form]}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
            required={["name", "date"].includes(key)}
            labelColor="text-ink"
          />
        ))}
        <InputField
          label="Status"
          name="status"
          type="select"
          value={form.status}
          onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
          labelColor="text-ink"
          defaultOptionLabel={null}
          options={[
            { value: "draft", label: "draft" },
            { value: "published", label: "published" },
            { value: "archived", label: "archived" },
          ]}
        />
        <div className="sm:col-span-2">
          <Button type="submit" color="teal" disabled={saving || !projectId}>
            {saving ? "Saving..." : "Save Event"}
          </Button>
        </div>
      </form>
    </AdminShell>
  );
}
